import React, { useEffect, useRef, useState } from "react";
import { FiltersProps } from "./DirectoryFilters";
import { Search, Navigation, Key, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import mapboxgl from 'mapbox-gl';
import { locationService } from "@/services/locationListingService";
import { Location, LocationResponse } from "@/types/location";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import "mapbox-gl/dist/mapbox-gl.css";

// Store the Mapbox token permanently
const MAPBOX_TOKEN = "pk.eyJ1Ijoic2F1bmFkaXAiLCJhIjoiY204c3RqNmx5MDM4YjJqcjFla3d4ZWZ2MCJ9.GmCtoyU-lih6Czx6sMsHRw";

// Define interfaces for type safety with mapbox
interface PointGeometry {
  type: "Point";
  coordinates: [number, number]; // Explicitly a tuple of [longitude, latitude]
}

interface MapboxFeature {
  type: "Feature";
  geometry: PointGeometry;
  properties: any;
}

interface DirectoryMapViewProps {
  filters: FiltersProps;
  onResultsChange?: (resultsExist: boolean, count?: number) => void;
}

const DirectoryMapView: React.FC<DirectoryMapViewProps> = ({ filters, onResultsChange }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const navigate = useNavigate();
  const markerRefs = useRef<{ [key: string]: mapboxgl.Marker }>({});
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [displayedListings, setDisplayedListings] = useState<Location[]>([]);
  const [mapStyleLoaded, setMapStyleLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });
  
  useEffect(() => {
    if (mapRef.current && !isInitialized) {
      initializeMap();
    }
  }, [isInitialized]);
  
  // Fetch listings from backend when filters or mapStyleLoaded change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapStyleLoaded) return;
    setLoading(true);
    setFetchError(null);
    locationService.getLocations({
      country: filters.selectedCountry,
      county: filters.selectedCounties?.join(",") || undefined,
      type: filters.type !== 'all' ? filters.type : undefined,
      search: filters.search || undefined,
      page: !filters.showAll ? pagination.page : undefined,
      per_page: !filters.showAll ? pagination.per_page : undefined,
      serviceTypes: filters.serviceTypes,
      placeTypes: filters.placeTypes,
      experiences: filters.experiences,
      amenities: filters.amenities,
      showAll: filters.showAll,
      location: filters.location
    })
      .then((response: LocationResponse) => {
        updateMap(response.data);
        setDisplayedListings(response.data);
        setPagination({
          page: response.meta.current_page,
          per_page: response.meta.per_page,
          total: response.meta.total,
          total_pages: response.meta.last_page
        });
        setLoading(false);
        if (onResultsChange) {
          onResultsChange(response.data.length > 0, response.meta.total);
        }
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
        setFetchError("Failed to load locations");
        setLoading(false);
        setDisplayedListings([]);
        if (onResultsChange) {
          onResultsChange(false, 0);
        }
      });
  }, [filters, mapStyleLoaded, pagination.page, pagination.per_page]);
  
  // Effect to add/remove user location marker on the map
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (userLocation) {
      // Remove previous marker if exists
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
      }
      // Create a new marker
      userMarkerRef.current = new mapboxgl.Marker({ color: '#F5542D' })
        .setLngLat([userLocation.lng, userLocation.lat])
        .addTo(mapInstanceRef.current);
    } else {
      // Remove marker if userLocation is cleared
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
    }
    // Clean up on unmount
    return () => {
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
    };
  }, [userLocation, mapInstanceRef.current]);
  
  const createPopupHTML = (item: Location) => {
    return `
      <div class="p-2">
        <h4 class="font-bold text-sauna-navy">${item.name || 'Unnamed Location'}</h4>
        <p class="text-sm text-gray-600">${item.location || 'Location not specified'}</p>
        <div class="flex items-center mt-1">
          <span class="text-sm text-sauna-orange font-medium">⭐ ${item.rating || 'N/A'}</span>
          <span class="mx-2">•</span>
          <span class="text-sm">${item.price || 'Price not available'}</span>
        </div>
        <p class="text-xs text-gray-500 mt-1">${(item.description || '').substring(0, 80)}...</p>
        <button class="mt-2 px-3 py-1 bg-sauna-orange text-white rounded-md text-sm hover:bg-sauna-orange/90 transition-colors w-full" onclick="window.location.href='/sauna/${item.id || '14'}'">
          View Details
        </button>
      </div>
    `;
  };
  
  // Get the first letter of location for markers
  const getLocationFirstLetter = (location: string | undefined): string => {
    if (!location) return '?';
    return location.charAt(0).toUpperCase();
  };
  
  // Replace getMapCenter with a simple function
  const getMapCenter = (countryValue: string) => {
    // You can expand this logic or fetch from API if needed
    if (countryValue === 'ireland') return { lng: -7.6, lat: 53.0 };
    if (countryValue === 'uk') return { lng: -2.0, lat: 54.5 };
    return { lng: -7.6, lat: 53.0 }; // Default to Ireland
  };
  
  const initializeMap = () => {
    if (!mapRef.current) return;
    
    // Set the mapbox token
    mapboxgl.accessToken = MAPBOX_TOKEN;
    
    try {
      const center = getMapCenter(filters.selectedCountry);
      
      // Initialize map
      mapInstanceRef.current = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/light-v10',
        center: [center.lng, center.lat],
        zoom: 6,
        scrollZoom: true
      });
      
      // Add navigation controls
      mapInstanceRef.current.addControl(
        new mapboxgl.NavigationControl(), 'top-right'
      );
      
      // Wait for the style to load before adding data
      mapInstanceRef.current.on('style.load', () => {
        console.log("Map style loaded");
        setMapStyleLoaded(true);
        setLoading(false);
      });
      
      // Set initialized flag
      mapInstanceRef.current.on('load', () => {
        console.log("Map loaded");
        setIsInitialized(true);
      });
      
      // Clean up the map instance when component unmounts
      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
        }
      };
    } catch (error) {
      console.error("Error initializing map:", error);
      setLoading(false);
    }
  };
  
  // Update map center when country changes
  useEffect(() => {
    if (mapInstanceRef.current && isInitialized) {
      const center = getMapCenter(filters.selectedCountry);
      mapInstanceRef.current.flyTo({
        center: [center.lng, center.lat],
        zoom: 6,
        duration: 1000
      });
    }
  }, [filters.selectedCountry, isInitialized]);
  
  // Function to update map with filtered data
  const updateMap = (data: Location[]) => {
    if (!mapInstanceRef.current || !mapStyleLoaded) {
      console.log("Map not ready for updating");
      return;
    }
    
    console.log("Updating map with data", data.length);
    
    try {
      // Remove previous sources and layers if they exist
      if (mapInstanceRef.current.getSource('saunas')) {
        mapInstanceRef.current.removeLayer('clusters');
        mapInstanceRef.current.removeLayer('cluster-count');
        mapInstanceRef.current.removeLayer('unclustered-point');
        mapInstanceRef.current.removeLayer('sauna-location-letters');
        mapInstanceRef.current.removeSource('saunas');
      }
      
      // Add new data source
      mapInstanceRef.current.addSource('saunas', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: data.map(item => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [item.lng, item.lat] as [number, number]
            },
            properties: {
              id: item.id,
              name: item.name || 'Unnamed Location',
              location: item.location || 'Location not specified',
              locationFirstLetter: getLocationFirstLetter(item.location),
              rating: item.rating || 0,
              description: createPopupHTML(item),
              icon: 'sauna'
            }
          }))
        } as GeoJSON.FeatureCollection<GeoJSON.Geometry>,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });
      
      // Add cluster layers
      mapInstanceRef.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'saunas',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#F5542D', // sauna-orange from tailwind config
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            15,   // radius for point_count < 5
            5,    // points
            25,   // radius for point_count >= 5
            10,   // points
            30    // radius for point_count >= 10
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#FFFFFF' // White border
        }
      });
      
      // Add cluster count text
      mapInstanceRef.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'saunas',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: {
          'text-color': '#FFFFFF' // White text
        }
      });
      
      // Add a layer for individual point circles
      mapInstanceRef.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'saunas',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#F5542D', // sauna-orange from tailwind config
          'circle-radius': 14,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#FFFFFF' // White border
        }
      });
      
      // Add a symbol layer for location first letters on unclustered points
      mapInstanceRef.current.addLayer({
        id: 'sauna-location-letters',
        type: 'symbol',
        source: 'saunas',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': '{locationFirstLetter}', // Use the first letter of location
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-offset': [0, 0],
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#FFFFFF' // White text
        }
      });
      
      // Handle click events for individual points
      mapInstanceRef.current.on('click', 'unclustered-point', (e) => {
        if (!e.features || e.features.length === 0) return;
        
        const feature = e.features[0] as unknown as MapboxFeature;
        const coordinates = feature.geometry.coordinates.slice() as [number, number];
        
        // Create a popup with the sauna details
        if (popupRef.current) {
          popupRef.current.remove();
        }
        
        popupRef.current = new mapboxgl.Popup({ offset: 25 })
          .setLngLat(coordinates)
          .setHTML(feature.properties.description)
          .addTo(mapInstanceRef.current!);
      });
      
      // Handle click events for clusters
      mapInstanceRef.current.on('click', 'clusters', (e) => {
        if (!e.features || e.features.length === 0) return;
        
        const feature = e.features[0] as unknown as MapboxFeature;
        const clusterId = feature.properties.cluster_id;
        
        const mapboxSource = mapInstanceRef.current!.getSource('saunas') as mapboxgl.GeoJSONSource;
        
        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          
          mapInstanceRef.current!.easeTo({
            center: feature.geometry.coordinates as [number, number],
            zoom: zoom + 0.5
          });
        });
      });
      
      // Change cursor on hover
      mapInstanceRef.current.on('mouseenter', 'unclustered-point', () => {
        mapInstanceRef.current!.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstanceRef.current.on('mouseleave', 'unclustered-point', () => {
        mapInstanceRef.current!.getCanvas().style.cursor = '';
      });
      
      mapInstanceRef.current.on('mouseenter', 'clusters', () => {
        mapInstanceRef.current!.getCanvas().style.cursor = 'pointer';
      });
      
      mapInstanceRef.current.on('mouseleave', 'clusters', () => {
        mapInstanceRef.current!.getCanvas().style.cursor = '';
      });
    } catch (error) {
      console.error("Error updating map:", error);
    }
  };
  
  // Function to fly to a specific sauna
  const flyToSauna = (sauna: Location) => {
    if (!mapInstanceRef.current) return;
    
    mapInstanceRef.current.flyTo({
      center: [sauna.lng, sauna.lat],
      zoom: 12,
      essential: true
    });
    
    setTimeout(() => {
      if (mapInstanceRef.current) {
        // Create a popup for the sauna
        if (popupRef.current) {
          popupRef.current.remove();
        }
        
        popupRef.current = new mapboxgl.Popup({ offset: 25 })
          .setLngLat([sauna.lng, sauna.lat])
          .setHTML(createPopupHTML(sauna))
          .addTo(mapInstanceRef.current);
      }
    }, 1000);
  };
  
  // Handler for 'Near Me' button on the map
  const handleMapNearMe = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(coords);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.flyTo({
            center: [coords.lng, coords.lat],
            zoom: 12,
            essential: true
          });
        }
      },
      (error) => {
        alert("Unable to retrieve your location.");
      }
    );
  };
  
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  return (
    <div className="relative w-full">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 space-y-4">
          <div className="bg-white rounded-lg shadow-sm p-3">
            <h3 className="font-medium text-sauna-navy mb-2">Search Results</h3>
            <p className="text-sm text-gray-500 mb-2">
              {pagination.total} {pagination.total === 1 ? "sauna" : "saunas"} found
            </p>
            
            <div className="h-[500px] overflow-y-auto pr-2">
              {displayedListings.map((sauna) => (
                <div 
                  key={sauna.id}
                  className="p-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => flyToSauna(sauna)}
                >
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-8 h-8 rounded-full bg-sauna-orange flex items-center justify-center text-white font-bold text-sm">
                        {getLocationFirstLetter(sauna.location)}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-sauna-navy">{sauna.name}</h4>
                      <p className="text-xs text-gray-500">{sauna.location}</p>
                      <div className="flex items-center mt-1">
                        <span className="text-xs text-sauna-orange">⭐ {sauna.rating}</span>
                        <span className="mx-1 text-gray-300">•</span>
                        <span className="text-xs">{sauna.price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {pagination.total_pages > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <Button
                    className="bg-white hover:bg-gray-50"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    className="bg-white hover:bg-gray-50"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.total_pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="md:col-span-3 relative">
          <div 
            ref={mapRef} 
            className="h-[600px] rounded-lg shadow-sm overflow-hidden"
            style={{ minHeight: '500px' }}
          />
          
          {/* Near Me floating button - move lower to avoid map controls */}
          <button
            className="absolute top-24 right-4 z-20 bg-white shadow-lg rounded-full p-2 hover:bg-sauna-orange/10 transition-colors border border-gray-200"
            title="Center map near me"
            onClick={handleMapNearMe}
          >
            <MapPin className="h-6 w-6 text-sauna-orange" />
          </button>
          
          {/* User location marker is now a real map marker, not an absolutely positioned div */}
          
          {(loading || !mapStyleLoaded) && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <LoadingSpinner size="lg" variant="default" />
                <p className="mt-4 text-sauna-navy font-medium">Loading map...</p>
              </div>
            </div>
          )}
          
          {/* Map legend */}
          <div className="absolute bottom-2 left-2 bg-white p-2 rounded shadow-sm">
            <div className="text-xs font-medium text-gray-700 mb-1">Map Legend</div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-sauna-orange flex items-center justify-center text-[10px] text-white font-bold">D</div>
              <span className="text-xs">Location Marker (first letter)</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-4 h-4 rounded-full bg-sauna-orange border border-white flex items-center justify-center text-[10px] text-white font-bold">3</div>
              <span className="text-xs">Cluster (number of locations)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectoryMapView;
